import { IconDownload, IconFilter, IconUserOff } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import type {
  ScalrTableColumn,
  ScalrTableData,
} from '@/components/customComponents'
import {
  BasicDatePicker,
  BasicTable,
  Loading,
  ScalrBreadcrumb,
  ScalrEmptyBox,
  SingleBranchSelect,
  TreeSelector,
  TreeSelectorChild,
} from '@/components/customComponents'
import ScalrBadge, { semanticStyles } from '@/components/ScalrBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast, useKpiGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { DateType } from '@/utils/date'

const getKpiPayoutColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'userFullName',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'kpi',
    header: t('targets.table.kpi'),
    type: 'text',
  },
  {
    accessorKey: 'branchName',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'period',
    header: t('targets.table.period'),
    type: 'text',
  },
  {
    accessorKey: 'targetValue',
    header: t('targets.table.target'),
    type: 'number',
  },
  {
    accessorKey: 'actualValue',
    header: 'Actual',
    type: 'number',
  },
  {
    accessorKey: 'bonusAmount',
    header: t('targets.table.bonus'),
    type: 'money',
  },
  {
    accessorKey: 'achieved',
    header: 'Achieved',
    type: 'text',
  },
  {
    accessorKey: 'progressPercent',
    header: 'Progress %',
    type: 'percentage',
  },
]

type FilterSelections = {
  branch?: string
  dateRange?: DateRange
  dateFilterType?: DateType
}

const PayoutSummary = () => {
  const { t } = useTranslation()
  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold mb-2">
        {t('targets.payoutSummary.title')}
      </h2>
      <div className="mb-4 text-sm">
        {t('targets.payoutSummary.description')}
      </div>
      <div className="flex flex-col gap-4 max-w-md">
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-2 divide-y divide-gray-200">
            <div className="flex items-center p-3 text-sm">
              {t('targets.payoutSummary.salary')}
            </div>
            <div className="flex items-center p-3 text-sm">$18.50</div>
            <div className="flex items-center p-3 text-sm">
              {t('targets.payoutSummary.hrsPerWeek')}
            </div>
            <div className="flex items-center p-3 text-sm">40</div>
            <div className="flex items-center p-3 text-sm">
              {t('targets.payoutSummary.weeks')}
            </div>
            <div className="flex items-center p-3 text-sm">6</div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 font-semibold grid grid-cols-2">
          <div className="flex items-center p-3 text-sm">
            {t('targets.payoutSummary.payout')}
          </div>
          <div className="flex items-center p-3 text-sm">$602</div>
        </div>
      </div>
    </div>
  )
}

const KPIPayoutPage = () => {
  const { t } = useTranslation()
  const [filterSelections, setFilterSelections] = useState<FilterSelections>({
    branch: undefined,
    dateRange: undefined,
    dateFilterType: 'quarterly',
  })
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>()

  const {
    fetchKpiAssignments,
    fetchMetadata,
    kpiAssignments,
    metadata,
    loading,
    error,
  } = useKpiGoalAssignments()

  // Show error toast if there is an error
  useEffect(() => {
    if (error) {
      toast({
        title: t('clarity.errors.somethingWentWrong'),
        description: error.message,
        variant: 'destructive',
      })
    }
  }, [error])

  // Fetch metadata and KPI assignments when branch selection changes
  useEffect(() => {
    if (filterSelections.branch) {
      fetchMetadata(filterSelections.branch)
      fetchKpiAssignments(filterSelections.branch)
    }
  }, [filterSelections.branch, fetchKpiAssignments, fetchMetadata])

  // Transform metadata.eligibleUsers to TreeSelector format
  const employeeFlatData: TreeSelectorChild[] = useMemo(() => {
    if (!metadata?.eligibleUsers) return []

    return metadata.eligibleUsers.map((user) => ({
      id: user.id.toString(),
      label: user.fullName || user.username,
      disabled: false,
    }))
  }, [metadata])

  // Handle date picker changes
  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setFilterSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleBranchSelect = useCallback((branchName: string) => {
    setFilterSelections((prev) => ({ ...prev, branch: branchName }))
    // Reset employee selection when branch changes
    setSelectedEmployeeId(undefined)
  }, [])

  const handleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployeeId(employeeId)
  }, [])

  // Transform assignments data for the table
  const assignmentsData: ScalrTableData[] = useMemo(
    () =>
      kpiAssignments && kpiAssignments.length > 0
        ? kpiAssignments.map((assignment) => ({
            userFullName: assignment.userFullName,
            kpi: `${assignment.kpiGroup} - ${assignment.kpiName} (${assignment.kpiUnit})`,
            branchName: assignment.branchName,
            period: `Q${assignment.quarter} ${assignment.year}`,
            targetValue: assignment.targetValue,
            actualValue: assignment.actualValue,
            bonusAmount: assignment.bonusAmount,
            achieved: (
              <ScalrBadge
                common
                conditionalStyle={() =>
                  assignment.achieved
                    ? semanticStyles.success
                    : semanticStyles.error
                }
              >
                {assignment.achieved ? 'Yes' : 'No'}
              </ScalrBadge>
            ) as any,
            progressPercent: assignment.progressPercent,
            createdAt: assignment.createdAt,
          }))
        : [],
    [kpiAssignments]
  )

  // Filter assignments based on selected employee and date range
  const filteredAssignmentsData = useMemo(() => {
    return assignmentsData.filter((assignment) => {
      // Filter by employee (required)
      if (selectedEmployeeId) {
        // Get the original assignment to access userId
        const originalAssignment = kpiAssignments?.find(
          (a) =>
            `${a.kpiGroup} - ${a.kpiName} (${a.kpiUnit})` === assignment.kpi &&
            a.branchName === assignment.branchName &&
            `Q${a.quarter} ${a.year}` === assignment.period
        )
        if (
          !originalAssignment ||
          originalAssignment.userId.toString() !== selectedEmployeeId
        ) {
          return false
        }
      }

      // Filter by date range (optional)
      if (filterSelections.dateRange?.from) {
        // Extract year and quarter from period string "Q1 2024"
        const periodMatch = assignment.period?.toString().match(/Q(\d+) (\d+)/)
        if (!periodMatch) return true

        const assignmentQuarter = parseInt(periodMatch[1])
        const assignmentYear = parseInt(periodMatch[2])

        const fromYear = filterSelections.dateRange.from.getFullYear()
        const fromQuarter =
          Math.floor(filterSelections.dateRange.from.getMonth() / 3) + 1

        if (filterSelections.dateFilterType === 'yearly') {
          if (assignmentYear !== fromYear) {
            return false
          }
        } else if (filterSelections.dateFilterType === 'quarterly') {
          if (
            assignmentYear !== fromYear ||
            assignmentQuarter !== fromQuarter
          ) {
            return false
          }
        }
      }

      return true
    })
  }, [assignmentsData, filterSelections, selectedEmployeeId, kpiAssignments])

  return (
    <Card className="w-full min-h-screen">
      <CardHeader>
        <CardTitle pageTitle>{t('targets.navigation.kpiPayout')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('targets.navigation.targets'), href: '/targets' },
          ]}
          title={t('targets.navigation.kpiPayout')}
        />

        {/* Filter Section */}
        <div className="my-3">
          <Card>
            <CardContent className="p-3">
              {loading ? (
                <Loading />
              ) : (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  {/* Filter Controls */}
                  <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                    <SingleBranchSelect
                      selectedBranch={filterSelections.branch}
                      onSelectBranch={handleBranchSelect}
                    />

                    {/* Show employee selector only when metadata is loaded */}
                    {metadata && (
                      <TreeSelector
                        data={employeeFlatData}
                        selectedId={selectedEmployeeId}
                        onSelect={handleEmployeeSelection}
                        placeholder={t('common.labels.selectEmployee')}
                        loading={loading}
                        noTree
                      />
                    )}

                    <BasicDatePicker
                      value={filterSelections.dateRange}
                      dateFilterType={filterSelections.dateFilterType}
                      onChange={handleDateChange}
                      noMonths
                      yearsUnselectable
                    />
                  </div>

                  {/* Action Button */}
                  <div className="flex gap-4 justify-center md:justify-start items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!filterSelections.branch || !selectedEmployeeId}
                    >
                      <IconDownload className="mr-2 h-4 w-4" />
                      {t('common.buttons.export')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Table Section - Show only when branch and employee are selected */}
        {filterSelections.branch && selectedEmployeeId ? (
          <>
            <div className="mt-3">
              <BasicTable
                data={filteredAssignmentsData}
                columns={getKpiPayoutColumns(t)}
                title={t('targets.navigation.kpiPayout')}
                loading={loading}
                enablePagination={true}
                itemsPerPage={6}
                showCard={true}
              />
            </div>

            {/* Payout Summary */}
            {/* {filteredAssignmentsData.length > 0 && <PayoutSummary />} */}
          </>
        ) : (
          <div className="mt-3">
            <Card>
              <CardContent>
                {!filterSelections.branch ? (
                  <ScalrEmptyBox
                    icon={IconFilter as any}
                    title={t('targets.emptyStates.selectBranchTitle')}
                    message={t('targets.emptyStates.selectBranchMessage')}
                    size="md"
                  />
                ) : !selectedEmployeeId ? (
                  <ScalrEmptyBox
                    icon={IconUserOff as any}
                    title={t('targets.emptyStates.selectEmployeeTitle')}
                    message={t('targets.emptyStates.selectEmployeeMessage')}
                    size="md"
                  />
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default KPIPayoutPage
